// Firebase Configuration for La Petite Épicerie - Simplified
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Configuration Firebase chargée depuis les variables d'environnement
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyD1pWK1-8qxrZLFYD3D1Wg4Ghfnj4G8h04",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "la-petite-epicerie-shop.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "la-petite-epicerie-shop",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "la-petite-epicerie-shop.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "1011438239336",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:1011438239336:web:ac430fd0233c18b77b45dd"
};

// VAPID Key for Web Push - chargé depuis l'environnement
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "BHcJCQHYAHrdvosLEpgfEa1FMNB61k32t9Ng6Eq0VvcEfROHcXThBU5i3xwK8NIZGByX4RtSxgT5CqZbpSuByNU";

// Initialize Firebase (singleton pattern)
let app = null;
let messaging = null;
let messagingSupported = null;

export const initializeFirebase = () => {
  try {
    if (!app) {
      app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    }
    return app;
  } catch (error) {
    console.error('Erreur initialisation Firebase:', error);
    return null;
  }
};

// Check if messaging is supported (async)
export const checkMessagingSupport = async () => {
  if (messagingSupported !== null) return messagingSupported;
  
  try {
    messagingSupported = await isSupported();
    return messagingSupported;
  } catch (error) {
    console.error('Erreur vérification support messaging:', error);
    messagingSupported = false;
    return false;
  }
};

export const getFirebaseMessaging = async () => {
  if (messaging) return messaging;
  
  try {
    const supported = await checkMessagingSupport();
    if (!supported) {
      console.log('Firebase Messaging non supporté sur ce navigateur');
      return null;
    }
    
    const firebaseApp = initializeFirebase();
    if (!firebaseApp) return null;
    
    messaging = getMessaging(firebaseApp);
    return messaging;
  } catch (error) {
    console.error('Erreur initialisation Firebase Messaging:', error);
    return null;
  }
};

// Request permission and get FCM token
export const requestNotificationPermission = async () => {
  try {
    // Check basic support
    if (typeof window === 'undefined') {
      console.log('Window non disponible');
      return null;
    }
    
    if (!('Notification' in window)) {
      console.log('Ce navigateur ne supporte pas les notifications');
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker non supporté');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    console.log('Permission notification:', permission);
    
    if (permission !== 'granted') {
      console.log('Permission de notification refusée');
      return null;
    }

    // Check messaging support
    const supported = await checkMessagingSupport();
    if (!supported) {
      console.log('Firebase Messaging non supporté');
      // Fall back to basic notifications
      return 'basic-notifications-only';
    }

    // Register service worker
    let registration;
    try {
      registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      
      if (!registration) {
        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service Worker enregistré:', registration);
      }
    } catch (swError) {
      console.error('Erreur Service Worker:', swError);
      return 'basic-notifications-only';
    }

    // Get messaging instance
    const messagingInstance = await getFirebaseMessaging();
    
    if (!messagingInstance) {
      console.log('Firebase Messaging non disponible');
      return 'basic-notifications-only';
    }

    // Get FCM token
    const token = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    console.log('FCM Token obtenu:', token ? token.substring(0, 20) + '...' : 'null');
    return token;

  } catch (error) {
    console.error('Erreur lors de la demande de permission:', error);
    // Return basic support if FCM fails
    if (Notification.permission === 'granted') {
      return 'basic-notifications-only';
    }
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  getFirebaseMessaging().then(messagingInstance => {
    if (!messagingInstance) {
      console.warn('Firebase Messaging not available for foreground messages');
      return;
    }
    
    onMessage(messagingInstance, (payload) => {
      console.log('Message reçu (foreground):', payload);
      callback(payload);
    });
  });
  
  return () => {}; // Return empty cleanup function
};

// Show notification manually (for foreground messages or basic notifications)
export const showLocalNotification = (title, body, icon = '/logo-192.png', data = {}) => {
  if (Notification.permission === 'granted') {
    try {
      new Notification(title, {
        body,
        icon,
        badge: '/logo-192.png',
        tag: data.tag || 'lpe-notification',
        data,
        vibrate: [200, 100, 200]
      });
    } catch (error) {
      console.error('Erreur affichage notification:', error);
    }
  }
};

export { VAPID_KEY };
